import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
  RedZoneObservationClassification,
  RedZoneObservationEntityType,
  RedZoneObservationLocation,
  RedZoneIncidentType,
  RedZoneReportStatus,
  RedZoneRiskLevel,
} from '../../api/redZone/post';

export type RedZoneSocketUpdateType =
  | 'RED_ZONE_OBSERVATION_CREATED'
  | 'RED_ZONE_OBSERVATION_UPDATED'
  | 'RED_ZONE_OBSERVATION_DELETED'
  | 'RED_ZONE_REPORT_CREATED';

export type RedZoneSocketUpdatePayload = {
  type: RedZoneSocketUpdateType;

  siteId?: string;
  associatedCompany?: string;

  observationId?: string;
  reportId?: string;

  entityType?: RedZoneObservationEntityType;
  classification?: RedZoneObservationClassification;

  observedAt?: string;
  location?: RedZoneObservationLocation;

  attachmentCount?: number;

  status?: RedZoneReportStatus;
  riskLevel?: RedZoneRiskLevel;
  incidentType?: RedZoneIncidentType;

  timestamp?: string;
};

export interface RedZoneRealtimeState {
  realtimeVersionBySiteId: Record<string, number>;

  lastUpdateBySiteId: Record<string, RedZoneSocketUpdatePayload>;
}

const initialState: RedZoneRealtimeState = {
  realtimeVersionBySiteId: {},
  lastUpdateBySiteId: {},
};

const redZoneSlice = createSlice({
  name: 'redZone',

  initialState,

  reducers: {
    redZoneSocketUpdateReceived: (
      state,
      action: PayloadAction<RedZoneSocketUpdatePayload>
    ) => {
      const payload = action.payload;
      const siteId = payload.siteId;

      if (!siteId) {
        return;
      }

      state.realtimeVersionBySiteId[siteId] =
        (state.realtimeVersionBySiteId[siteId] || 0) + 1;

      state.lastUpdateBySiteId[siteId] = payload;
    },

    clearRedZoneRealtimeForSite: (state, action: PayloadAction<string>) => {
      delete state.realtimeVersionBySiteId[action.payload];
      delete state.lastUpdateBySiteId[action.payload];
    },
  },
});

export const { redZoneSocketUpdateReceived, clearRedZoneRealtimeForSite } =
  redZoneSlice.actions;

export default redZoneSlice.reducer;

export const selectRedZoneRealtimeVersion = (
  state: {
    redZone?: RedZoneRealtimeState;
  },
  siteId?: string
) => {
  if (!siteId) {
    return 0;
  }

  return state.redZone?.realtimeVersionBySiteId?.[siteId] ?? 0;
};

export const selectLastRedZoneUpdate = (
  state: {
    redZone?: RedZoneRealtimeState;
  },
  siteId?: string
) => {
  if (!siteId) {
    return undefined;
  }

  return state.redZone?.lastUpdateBySiteId?.[siteId];
};
